import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, Section } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { fetchPosts, formatPostDate, isBlogConfigured } from "@/lib/blog";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [state, setState] = useState(isBlogConfigured() ? "loading" : "unconfigured");

  useEffect(() => {
    if (!isBlogConfigured()) return;
    let live = true;
    fetchPosts()
      .then((data) => {
        if (!live) return;
        setPosts(data);
        setState(data.length ? "ready" : "empty");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, []);

  return (
    <div>
      <SEO
        path="/blog"
        title="Insights"
        description="Market commentary and plain-spoken explainers on mutual funds, SIPs, retirement planning and corporate treasury from Rao Investment Solutions."
      />
      <PageHero
        label="Insights"
        title="Notes on markets, money and planning."
        intro="Plain-spoken commentary and explainers — written for people making real decisions, not for the algorithm."
        breadcrumbs={[{ name: "Home", to: "/" }, { name: "Insights" }]}
      />

      <Section className="!pt-0">
        {state === "loading" && <Message>Loading posts…</Message>}

        {state === "unconfigured" && (
          <Message>
            The blog is not connected yet. Once the CMS is configured, posts published there appear
            here automatically.
          </Message>
        )}

        {state === "error" && (
          <Message>
            Posts could not be loaded right now. Please try again shortly, or{" "}
            <Link to="/contact" className="text-orange hover:underline">
              get in touch
            </Link>{" "}
            if you were looking for something specific.
          </Message>
        )}

        {state === "empty" && <Message>The first posts are on their way. Check back shortly.</Message>}

        {state === "ready" && (
          <div className="max-w-4xl divide-y divide-navy/10 border-t border-navy/10">
            {posts.map((post, i) => (
              <Reveal key={post._id} delay={Math.min(i * 0.05, 0.2)}>
                <article>
                  <Link to={`/blog/${post.slug}`} className="group block py-10">
                    <div className="flex flex-wrap items-center gap-3 font-data text-xs text-slate">
                      {post.category && (
                        <span className="uppercase tracking-[0.18em] text-orange">{post.category}</span>
                      )}
                      {post.publishedAt && <span>{formatPostDate(post.publishedAt)}</span>}
                      {post.readingTime > 0 && <span>· {post.readingTime} min read</span>}
                    </div>
                    <h2 className="mt-3 font-serif text-2xl md:text-3xl text-navy tracking-tight group-hover:text-orange transition-colors">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="mt-3 font-sans text-base text-slate leading-relaxed max-w-2xl">
                        {post.excerpt}
                      </p>
                    )}
                    <span className="mt-4 inline-flex items-center gap-2 font-sans text-sm text-navy">
                      Read
                      <ArrowRight
                        className="w-4 h-4 transition-transform group-hover:translate-x-1"
                        strokeWidth={1.5}
                      />
                    </span>
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

const Message = ({ children }) => (
  <p className="max-w-2xl font-sans text-base text-slate leading-relaxed">{children}</p>
);
